import Header from "./Header";
import Footer from "./Footer";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buildLevelMap } from "../utils/referralTree";
import {
  fetchReferralTree,
  selectReferralTree,
} from "../redux/slices/referralTreeSlice";
import {
  fetchUserById,
  selectUserByIdMap,
  selectUserByIdStatus,
  selectUserByIdError,
} from "../redux/slices/profileSlice";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/**
 * Flatten the nested tree into an ordered array of row descriptors.
 * Each row carries:
 *   { userId, level, hasChildren, parentId }
 *
 * Collapsed nodes hide their subtrees.
 */
function flattenTree(node, collapsed, parentId = null, result = []) {
  if (!node) return result;

  result.push({
    userId: node.userId,
    level: node.level,
    hasChildren: node.children && node.children.length > 0,
    parentId,
  });

  if (!collapsed.has(node.userId) && node.children) {
    for (const child of node.children) {
      flattenTree(child, collapsed, node.userId, result);
    }
  }

  return result;
}

// Badge colours matching original mock-ups
const RANK_STYLES = {
  ruby: { bg: "#fee2e2", color: "#b91c1c", label: "Ruby" },
  pearl: { bg: "#f0f9ff", color: "#0369a1", label: "Pearl" },
  jade: { bg: "#dcfce7", color: "#15803d", label: "Jade" },
  diamond: { bg: "#ede9fe", color: "#6d28d9", label: "Double Diamond" },
  legend: { bg: "#fef9c3", color: "#b45309", label: "Legend" },
  icon: { bg: "#fce7f3", color: "#9d174d", label: "Icon" },
  unranked: { bg: "#f3f4f6", color: "#6b7280", label: "Unranked" },
};

/** Pick a deterministic rank from userId so demo rows look varied */
function pickRank(userId = "") {
  const keys = Object.keys(RANK_STYLES);
  const idx = userId.charCodeAt(0) % keys.length;
  return RANK_STYLES[keys[idx]];
}

/** Whether a user is considered "active" – derive from userMap or fall back */
function isActive(userInfo) {
  if (!userInfo) return false;
  // Support various API shapes: active / isActive / status === 'active'
  if (typeof userInfo.active === "boolean") return userInfo.active;
  if (typeof userInfo.isActive === "boolean") return userInfo.isActive;
  if (typeof userInfo.status === "string")
    return userInfo.status.toLowerCase() === "active";
  return true; // optimistic default when we have user data
}

// ─────────────────────────────────────────────
// ReferralTable
// ─────────────────────────────────────────────

function ReferralTable({ treeRoot, userMap }) {
  const [collapsed, setCollapsed] = useState(new Set());

  const toggleCollapse = useCallback((userId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  }, []);

  const rows = useMemo(
    () => flattenTree(treeRoot, collapsed),
    [treeRoot, collapsed],
  );

  if (!treeRoot) {
    return (
      <div className="container bg-success-soft p-4 rounded-4 shadow text-center text-muted py-5">
        No Referral Data Available.
      </div>
    );
  }

  return (
    <div className="container bg-success-soft p-4 pt-7 rounded-4 shadow">
      {rows.map((row) => {
        const userInfo = userMap?.[row.userId];
        const rank = pickRank(row.userId);
        const active = isActive(userInfo);
        const isNew = userInfo?.isNew ?? false;
        const isCollapsed = collapsed.has(row.userId);

        // Display name: prefer full name, fall back to userId
        const displayName =
          userInfo?.fullName ||
          userInfo?.name ||
          `${userInfo?.firstName ?? ""} ${userInfo?.lastName ?? ""}`.trim() ||
          row.userId;

        // Username / handle
        const username =
          userInfo?.username ||
          userInfo?.userName ||
          userInfo?.handle ||
          row.userId;

        // Tag colour cycles by level
        const tagColors = ["green", "blue", "teal", "purple", "orange"];
        const tagColor = tagColors[(row.level - 1) % tagColors.length];

        return (
          <div
            key={row.userId}
            className={`mlm-row${active ? "" : " active"}`}
            style={{ paddingLeft: `${(row.level - 1) * 1.5}rem` }}
          >
            {/* ── User cell ── */}
            <div className={`user-cell level-${row.level}`}>
              <div className="tree-line">
                {row.hasChildren && (
                  <span
                    className="plus"
                    role="button"
                    title={isCollapsed ? "Expand" : "Collapse"}
                    onClick={() => toggleCollapse(row.userId)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {isCollapsed ? "+" : "−"}
                  </span>
                )}
                <span className={`tag ${tagColor}`}>{username}</span>
                {row.level !== 0 && (
                  <span
                    className="rank"
                    style={{
                      backgroundColor: rank.bg,
                      color: rank.color,
                      padding: "2px 10px",
                      borderRadius: "999px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    Level {row.level}
                  </span>
                )}
              </div>
              <div className="name text-capitalize">
                {displayName || (
                  <span className="text-muted fst-italic">Loading…</span>
                )}
                {isNew && <span className="new ms-1">New</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// Network (page)
// ─────────────────────────────────────────────

function Network() {
  const dispatch = useDispatch();

  const treeRes = useSelector(selectReferralTree);
  const userMap = useSelector(selectUserByIdMap);

  const [selectedLevel, setSelectedLevel] = useState(null);

  const LEVEL_LABELS = {
    7: { label: "Star", color: "bg-warning bg-opacity-10 text-warning" },
    10: { label: "Silver", color: "bg-secondary bg-opacity-10 text-secondary" },
    11: { label: "Gold", color: "bg-warning bg-opacity-10 text-warning" },
    12: { label: "Platinum", color: "bg-primary bg-opacity-10 text-primary" },
    13: { label: "Diamond", color: "bg-info bg-opacity-10 text-info" },
    14: { label: "Blue Diamond", color: "bg-info bg-opacity-10 text-info" },
  };

  const mlmPossibleUsers = (level) => {
    let total = 0;
    for (let i = 1; i <= level; i++) total += Math.pow(3, i);
    return total;
  };

  const LEVEL_PV = {
    1: "1",
    2: "3",
    3: "9",
    4: "27",
    5: "81",
    6: "243",
    7: "729",
    8: "2,187",
    9: "6,561",
    10: "19,683",
    11: "59,049",
    12: "1,77,147",
    13: "5,31,441",
    14: "15,94,323",
  };

  const userId = useSelector((s) => s.auth.userId);
  useEffect(() => {
    if (userId) dispatch(fetchReferralTree({ userId }));
  }, [dispatch, userId]);

  // Support both { data: tree } and bare tree shapes
  const treeRoot = treeRes?.data ?? treeRes;

  const levelMap = useMemo(() => buildLevelMap(treeRoot), [treeRoot]);

  // Fetch user details for every node in the tree (once)
  useEffect(() => {
    if (!treeRoot) return;

    const collectIds = (node) => {
      if (!node) return;
      if (!userMap[node.userId]) dispatch(fetchUserById(node.userId));
      node.children?.forEach(collectIds);
    };

    collectIds(treeRoot);
  }, [treeRoot, dispatch, userMap]);

  const openLevelFilter = (level) => {
    setSelectedLevel((prev) => (prev === level ? null : level));
  };

  return (
    <>
      <Header />

      <main>
        <section className="container mt-4 mb-6 pb-2">
          <div className="row g-2">
            <div>
              <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                Network / Earning History
              </span>
              <h2 className="fw-semibold ls-sm text-success fs-3 lh-1">
                Your Referral Network / Points Earning History
              </h2>
              <div className="mt-n3 pt-2 mb-2">
                <img src="/assets/images/line-shap.webp" width="150" alt="" />
              </div>
            </div>

            <div className="col-12">
              <div className="card shadow rounded-4 border border-light">
                <div className="card-body">
                  <div className="row g-1 text-center">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(
                      (level) => {
                        const count = levelMap?.[level]?.count ?? 0;
                        const isActive = selectedLevel === level;
                        const pv = LEVEL_PV[level];
                        const meta = LEVEL_LABELS[level];
                        const possible = mlmPossibleUsers(level);

                        return (
                          <div key={level} className="col-lg-2 col-md-4 col-6">
                            <div
                              className={`p-3 border rounded-4 shadow cursor-pointer h-100 d-flex flex-column justify-content-center ${
                                isActive
                                  ? "bg-success text-white border-success"
                                  : "bg-white"
                              }`}
                              role="button"
                              onClick={() => openLevelFilter(level)}
                            >
                              <div>
                                {meta && (
                                  <span
                                    className={`badge rounded-pill px-3 mb-2 w-auto d-inline-block ${
                                      isActive
                                        ? "bg-white text-success"
                                        : `${meta.color} text-white`
                                    }`}
                                  >
                                    {meta.label}
                                  </span>
                                )}
                              </div>

                              <h4
                                className={`fw-semibold ls-sm lh-1 m-0 ${isActive ? "text-white" : "text-primary"}`}
                              >
                                Level {level}
                              </h4>

                              <p
                                className={`mb-2 small ${isActive ? "text-white opacity-75" : "text-muted"}`}
                              >
                                {pv} PV Required
                              </p>

                              {/* MLM structure count */}
                              {/* <div
                                className={`fw-semibold fs-5 lh-1 mb-0 ${isActive ? "text-white" : "text-dark"}`}
                              >
                                {possible.toLocaleString("en-IN")}
                              </div>
                              <div
                                className={`small mb-2 ${isActive ? "text-white opacity-75" : "text-muted"}`}
                              >
                                {" "}
                                Members
                              </div> */}

                              <div>
                                <div
                                  className={`badge px-4 rounded-pill ${isActive ? "bg-white text-success" : "bg-success"}`}
                                >
                                  {count} Joined
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}

                    <div className="col-lg-8 col-md-4 col-6">
                      <div className="alert alert-danger rounded-4 px-5 py-3 lh-sm h-100 d-flex justify-content-center flex-column">
                        <div class="avatar-group">
                          <span class="avatar">
                            <img
                              alt="avatar"
                              src="/assets/images/profile.png"
                              class="rounded-circle"
                            />
                          </span>
                          <span class="avatar">
                            <img
                              alt="avatar"
                              src="/assets/images/profile.png"
                              class="rounded-circle"
                            />
                          </span>
                          <span class="avatar">
                            <img
                              alt="avatar"
                              src="/assets/images/profile.png"
                              class="rounded-circle"
                            />
                          </span>
                        </div>
                        <span className="fw-semibold mt-3">
                          You can add up to 3 direct referrals.
                        </span>
                        Additional referrals grow automatically into the next
                        levels.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Referral Table driven by real tree ── */}
        <section className="pb-7">
          <div className="container">
            <ReferralTable treeRoot={treeRoot} userMap={userMap} />
          </div>
        </section>

        {/* Level Management section (unchanged) */}
        <section className="bg-light py-7">
          <div className="container mb-1">
            <div className="row">
              <div className="offset-xl-1 col-xl-10 col-md-12 col-12">
                <div className="row text-center">
                  <div className="col-md-12 px-lg-8 mb-4">
                    <span className="text-uppercase text-primary fw-semibold ls-md">
                      Level Management
                    </span>
                    <h2 className="fw-normal ls-sm text-dark lh-sm">
                      Grow Your Network in{" "}
                      <span className="text-dark fw-bold">
                        3 Powerful Stages
                      </span>
                    </h2>
                  </div>
                </div>
                <div className="row gy-6">
                  <div className="col-md-4 col-12">
                    <div className="text-center">
                      <div className="icon-shape icon-lg border border-primary border-2 fs-3 rounded-circle mb-4 process-line text-primary smooth-shadow-md">
                        1
                      </div>
                      <h4 className="text-dark">Add Direct Referrals</h4>
                      <p className="mb-0 px-3 lh-sm">
                        Add <u>up to 3 direct referrals</u>, forming the base of
                        your network growth.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 col-12">
                    <div className="text-center">
                      <div className="icon-shape icon-lg border border-primary border-2 fs-3 rounded-circle mb-4 process-line text-primary smooth-shadow-md">
                        2
                      </div>
                      <h4 className="text-dark">Automatic Level Expansion</h4>
                      <p className="mb-0 px-3 lh-sm">
                        Network automatically expands into{" "}
                        <u>multiple levels</u> as members continue.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 col-12">
                    <div className="text-center">
                      <div className="icon-shape icon-lg border border-primary border-2 fs-3 rounded-circle mb-4 text-primary smooth-shadow-md">
                        3
                      </div>
                      <h4 className="text-dark">
                        Unlock Milestones &amp; Rewards
                      </h4>
                      <p className="mb-0 px-3 lh-sm">
                        Unlock achievements at{" "}
                        <u>Level 7, 10, 11, 12, 13, &amp; 14</u> with rewards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Network;
